import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, Trash2, ToggleLeft, ToggleRight } from 'lucide-react';
import { Text, Badge, Icon } from '@components/base/display';
import { Button } from '@components/base/actions';
import { TextField, Select, Checkbox } from '@components/base/inputs';
import { HStack, VStack } from '@components/base/layout';
import { Table } from '@components/base/data';
import { Alert, Modal, Spinner, useToast } from '@components/base/feedback';
import { tenantsService } from '../../api/tenantsService';
import { modulesService } from '../../api/modulesService';
import type { TenantModule } from '../../api/tenantsService';

interface TenantModulesTabProps {
  readonly tenantId: string;
}

export function TenantModulesTab({ tenantId }: TenantModulesTabProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // ---- State ----
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<TenantModule | null>(null);

  // Assign modal form state
  const [selectedModuleSlug, setSelectedModuleSlug] = useState('');
  const [assignEnabled, setAssignEnabled] = useState(true);
  const [assignConfig, setAssignConfig] = useState('');

  // ---- Data Fetching ----

  const {
    data: modulesData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['admin', 'tenants', tenantId, 'modules'],
    queryFn: () => tenantsService.listModules(tenantId),
  });

  const { data: availableModulesData } = useQuery({
    queryKey: ['admin', 'modules'],
    queryFn: () => modulesService.list(),
    enabled: assignModalOpen,
  });

  const assignedModules = modulesData?.data ?? [];
  const availableModules = availableModulesData?.data ?? [];

  // Filter out already-assigned modules from the assign dropdown
  const assignedModuleSlugs = new Set(assignedModules.map((m) => m.slug));
  const unassignedModules = availableModules.filter(
    (m) => !assignedModuleSlugs.has(m.slug),
  );

  const moduleOptions = unassignedModules.map((m) => ({
    value: m.slug,
    label: `${m.name} (${m.slug})`,
  }));

  // ---- Mutations ----

  const assignMutation = useMutation({
    mutationFn: (data: {
      moduleSlug: string;
      is_enabled: boolean;
      config: Record<string, unknown> | null;
    }) =>
      tenantsService.assignModule(tenantId, data.moduleSlug, {
        is_enabled: data.is_enabled,
        config: data.config,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['admin', 'tenants', tenantId, 'modules'],
      });
      toast({ variant: 'success', title: 'Module assigned successfully.' });
      handleCloseAssignModal();
    },
  });

  const toggleMutation = useMutation({
    mutationFn: (data: { moduleSlug: string; is_enabled: boolean }) =>
      tenantsService.updateModule(tenantId, data.moduleSlug, {
        is_enabled: data.is_enabled,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['admin', 'tenants', tenantId, 'modules'],
      });
      toast({ variant: 'success', title: 'Module status toggled.' });
    },
  });

  const removeMutation = useMutation({
    mutationFn: (moduleSlug: string) =>
      tenantsService.removeModule(tenantId, moduleSlug),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['admin', 'tenants', tenantId, 'modules'],
      });
      toast({ variant: 'success', title: 'Module removed from tenant.' });
      setRemoveTarget(null);
    },
  });

  // ---- Handlers ----

  const handleCloseAssignModal = () => {
    setAssignModalOpen(false);
    setSelectedModuleSlug('');
    setAssignEnabled(true);
    setAssignConfig('');
    assignMutation.reset();
  };

  const handleAssignSubmit = () => {
    let parsedConfig: Record<string, unknown> | null = null;
    if (assignConfig.trim()) {
      try {
        parsedConfig = JSON.parse(assignConfig) as Record<string, unknown>;
      /* v8 ignore start */
      } catch {
        return; // Invalid JSON, don't submit
      }
      /* v8 ignore stop */
    }
    assignMutation.mutate({
      moduleSlug: selectedModuleSlug,
      is_enabled: assignEnabled,
      config: parsedConfig,
    });
  };

  const handleToggleEnabled = (mod: TenantModule) => {
    toggleMutation.mutate({
      moduleSlug: mod.slug,
      is_enabled: !mod.is_enabled,
    });
  };

  const handleCloseRemoveModal = () => {
    setRemoveTarget(null);
    removeMutation.reset();
  };

  const handleRemoveConfirm = () => {
    /* v8 ignore start */
    if (removeTarget) {
      removeMutation.mutate(removeTarget.slug);
    }
    /* v8 ignore stop */
  };

  // ---- Helpers ----

  const isValidJson = (value: string): boolean => {
    if (!value.trim()) return true;
    try {
      JSON.parse(value);
      return true;
    } catch {
      return false;
    }
  };

  // ---- Render: Loading ----

  if (isLoading) {
    return (
      <VStack align="center" justify="center" gap={2} className="py-12">
        <Spinner size="lg" />
        <Text color="muted">Loading modules...</Text>
      </VStack>
    );
  }

  // ---- Render: Error ----

  if (error) {
    return (
      <Alert variant="error" title="Failed to load modules">
        {error instanceof Error
          ? error.message
          : 'An unexpected error occurred while loading tenant modules.'}
      </Alert>
    );
  }

  // ---- Render: Main ----

  return (
    <VStack gap={6}>
      {/* Header */}
      <HStack justify="between" align="center">
        <Text weight="medium">Assigned Modules</Text>
        <Button
          variant="primary"
          size="sm"
          leftIcon={<Icon icon={Plus} size="sm" />}
          onClick={() => setAssignModalOpen(true)}
        >
          Assign Module
        </Button>
      </HStack>

      {/* Modules Table */}
      {assignedModules.length === 0 ? (
        <Alert variant="info" title="No modules assigned">
          This tenant has no modules assigned yet. Click &quot;Assign Module&quot; to
          get started.
        </Alert>
      ) : (
        <Table>
          <Table.Head>
            <Table.Row>
              <Table.HeaderCell>Name</Table.HeaderCell>
              <Table.HeaderCell>Slug</Table.HeaderCell>
              <Table.HeaderCell>Version</Table.HeaderCell>
              <Table.HeaderCell align="center">Status</Table.HeaderCell>
              <Table.HeaderCell>Assigned At</Table.HeaderCell>
              <Table.HeaderCell align="right">Actions</Table.HeaderCell>
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {assignedModules.map((mod) => (
              <Table.Row key={mod.id}>
                <Table.Cell>
                  <Text weight="medium">{mod.name}</Text>
                </Table.Cell>
                <Table.Cell>
                  <Text size="sm" color="muted">
                    {mod.slug}
                  </Text>
                </Table.Cell>
                <Table.Cell>
                  <Text size="sm" color="muted">
                    {mod.version ?? '--'}
                  </Text>
                </Table.Cell>
                <Table.Cell align="center">
                  <Badge
                    variant={mod.is_enabled ? 'success' : 'default'}
                    size="sm"
                  >
                    {mod.is_enabled ? 'Enabled' : 'Disabled'}
                  </Badge>
                </Table.Cell>
                <Table.Cell>
                  <Text size="sm" color="muted">
                    {new Date(mod.assigned_at).toLocaleDateString()}
                  </Text>
                </Table.Cell>
                <Table.Cell align="right">
                  <HStack gap={1} justify="end">
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={`Toggle ${mod.name}`}
                      leftIcon={
                        <Icon
                          icon={mod.is_enabled ? ToggleRight : ToggleLeft}
                          size="sm"
                          color={mod.is_enabled ? 'success' : 'muted'}
                        />
                      }
                      onClick={() => handleToggleEnabled(mod)}
                      disabled={toggleMutation.isPending}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={`Edit ${mod.name} configuration`}
                      leftIcon={<Icon icon={Pencil} size="sm" />}
                      onClick={() => navigate(`/admin/tenants/${tenantId}/modules/${mod.slug}`)}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={`Remove ${mod.name}`}
                      leftIcon={<Icon icon={Trash2} size="sm" color="error" />}
                      onClick={() => setRemoveTarget(mod)}
                    />
                  </HStack>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}

      {/* Assign Module Modal */}
      <Modal open={assignModalOpen} onClose={handleCloseAssignModal} size="md">
        <Modal.Header title="Assign Module to Tenant" />
        <Modal.Body>
          <VStack gap={4}>
            <Select
              label="Module"
              placeholder="Select a module..."
              options={moduleOptions}
              value={selectedModuleSlug}
              onChange={(value) => setSelectedModuleSlug(value as string)}
              required
            />
            <Checkbox
              label="Enabled"
              checked={assignEnabled}
              onChange={(e) => setAssignEnabled(e.target.checked)}
            />
            <TextField
              label="Configuration (JSON)"
              placeholder='{"key": "value"}'
              value={assignConfig}
              onChange={(e) => setAssignConfig(e.target.value)}
              helperText="Optional JSON configuration for this module."
              error={!isValidJson(assignConfig)}
              errorMessage={
                isValidJson(assignConfig) ? undefined : 'Invalid JSON format.'
              }
            />
            {assignMutation.isError && (
              <Alert variant="error" title="Failed to assign module">
                {assignMutation.error instanceof Error
                  ? assignMutation.error.message
                  : 'An unexpected error occurred.'}
              </Alert>
            )}
          </VStack>
        </Modal.Body>
        <Modal.Footer>
          <HStack gap={2} justify="end">
            <Button
              variant="secondary"
              onClick={handleCloseAssignModal}
              disabled={assignMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleAssignSubmit}
              loading={assignMutation.isPending}
              disabled={!selectedModuleSlug || !isValidJson(assignConfig)}
            >
              Assign Module
            </Button>
          </HStack>
        </Modal.Footer>
      </Modal>

      {/* Remove Confirmation Modal */}
      <Modal
        open={removeTarget !== null}
        onClose={handleCloseRemoveModal}
        size="sm"
      >
        <Modal.Header title="Remove Module" />
        <Modal.Body>
          <VStack gap={3}>
            <Text>
              Are you sure you want to remove the module{' '}
              <Text as="span" weight="bold">
                {removeTarget?.name}
              </Text>{' '}
              from this tenant? This action cannot be undone.
            </Text>
            {removeMutation.isError && (
              <Alert variant="error" title="Failed to remove module">
                {removeMutation.error instanceof Error
                  ? removeMutation.error.message
                  : 'An unexpected error occurred.'}
              </Alert>
            )}
          </VStack>
        </Modal.Body>
        <Modal.Footer>
          <HStack gap={2} justify="end">
            <Button
              variant="secondary"
              onClick={handleCloseRemoveModal}
              disabled={removeMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleRemoveConfirm}
              loading={removeMutation.isPending}
            >
              Remove
            </Button>
          </HStack>
        </Modal.Footer>
      </Modal>
    </VStack>
  );
}
